import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { devLog } from '@/utils/logger';
import type { 
  CompensationRule, 
  CompensationRuleQuery,
  CompensationCalculationInput,
  CompensationCalculationResult,
  PayrollPreviewInput,
  PayrollPreviewResult,
  PaidLeaveEntry,
  RateContext
} from './types';
import { calculationEngine } from './calculationEngine';
import { timeEntryAnalytics } from '@/services/timeEntries';
import type { SplitShiftConfig } from '@/types/companySettings';
import { DEFAULT_SPLIT_SHIFT_CONFIG } from '@/types/companySettings';
import { serviceChargeService } from './serviceChargeService';

// Enhanced helper function to log assignment history
// Added 'reactivated' action type for FMEA F3 mitigation
const logAssignmentHistory = async (
  action: 'created' | 'updated' | 'assigned' | 'unassigned' | 'deactivated' | 'bulk_assigned',
  ruleId: string,
  profileIds: string | string[],
  reason?: string,
  additionalData?: Record<string, any>
) => {
  try {
    // Fetch rule details and performer's profile ID in parallel
    const [{ data: rule }, { data: performerProfile }] = await Promise.all([
      supabase
        .from('compensation_rules')
        .select('rule_name, rule_type, company_id')
        .eq('id', ruleId)
        .single(),
      supabase
        .from('profiles')
        .select('id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id ?? '')
        .maybeSingle(),
    ]);

    if (!rule) return;

    if (!performerProfile) {
      console.warn('[compensationService] No profile found for current user — skipping history log');
      return;
    }

    // Normalize profileIds to array
    const profileIdArray = Array.isArray(profileIds) ? profileIds : [profileIds];
    
    // Create history entries for each profile
    const historyEntries = profileIdArray.map(profileId => ({
      company_id: rule.company_id,
      profile_id: profileId,
      compensation_rule_id: ruleId,
      action,
      rule_name: rule.rule_name,
      rule_type: rule.rule_type,
      performed_by: performerProfile.id,
      old_values: additionalData?.old_values || {},
      new_values: additionalData?.new_values || {
        action_type: action,
        affected_employees: profileIdArray.length
      },
      reason
    }));

    const { error: insertError } = await supabase
      .from('compensation_assignment_history')
      .insert(historyEntries);

    if (insertError) {
      console.warn('[compensationService] Assignment history insert failed:', insertError.message, insertError.details);
    }
      
  } catch (error) {
    console.warn('[compensationService] Failed to log assignment history:', error);
  }
};

export const compensationService = {
  /**
   * Get all compensation rules for the current user's company
   */
  getCompanyCompensationRules: async (): Promise<CompensationRule[]> => {
    const { data, error } = await supabase
      .from('compensation_rules')
      .select('*')
      .order('rule_type', { ascending: true })
      .order('priority_order', { ascending: true });

    if (error) {
      console.error('[compensationService] Error fetching company compensation rules:', error);
      throw new Error(`Failed to fetch company compensation rules: ${error.message}`);
    }

    return data || [];
  },

  /**
   * Get compensation rules for a profile - Updated to use enhanced method
   */
  getEmployeeCompensationRules: async (query: CompensationRuleQuery): Promise<CompensationRule[]> => {
    // Use the enhanced method as the primary approach
    return await compensationService.getEmployeeCompensationRulesEnhanced(query);
  },

  /**
   * Calculate compensation for an employee based on hours worked
   */
  calculateHourlyCompensation: async (input: CompensationCalculationInput): Promise<CompensationCalculationResult> => {
    try {
      // Get active compensation rules for the profile using enhanced method
      const rules = await compensationService.getEmployeeCompensationRulesEnhanced({
        profileId: input.profileId,
        effectiveDate: input.calculationDate || new Date(),
        activeOnly: true
      });

      // Extract salary rule weekly_hours if present to populate rate context
      const salaryRule = rules.find(r => r.rule_type === 'salary');
      const enhancedContext: RateContext = {
        ...input.rateContext,
        salaryRuleWeeklyHours: salaryRule?.weekly_hours ?? input.rateContext?.salaryRuleWeeklyHours
      };

      // If overtimeHours provided but not dailyOvertimeHours, treat as single block
      const enhancedInput = { ...input, rateContext: enhancedContext };
      if (input.overtimeHours && !input.dailyOvertimeHours) {
        enhancedInput.dailyOvertimeHours = [input.overtimeHours];
      }

      // Use calculation engine to compute compensation with enhanced context
      // Phase 4: Pass analytics if available for allowance calculations
      return calculationEngine.calculateCompensation(enhancedInput, rules, undefined);
    } catch (error) {
      console.error('[compensationService] Error calculating compensation:', error);
      throw new Error(`Failed to calculate compensation: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  /**
   * Validate compensation rules for conflicts and overlaps
   */
  validateCompensationRules: async (profileId: string, newRule: Partial<CompensationRule>): Promise<{ valid: boolean; errors: string[] }> => {
    try {
      // Use enhanced method to get existing rules
      const existingRules = await compensationService.getEmployeeCompensationRulesEnhanced({
        profileId,
        ruleType: newRule.rule_type as any,
        activeOnly: true
      });

      const errors: string[] = [];

      // Check for overlapping date ranges
      if (newRule.effective_from && newRule.rule_type) {
        const overlappingRules = existingRules.filter(rule => {
          if (rule.id === newRule.id) return false; // Skip self when updating
          
          const newStart = new Date(newRule.effective_from!);
          const newEnd = newRule.effective_to ? new Date(newRule.effective_to) : null;
          const existingStart = new Date(rule.effective_from);
          const existingEnd = rule.effective_to ? new Date(rule.effective_to) : null;

          // Check for date overlap
          if (newEnd === null && existingEnd === null) return true; // Both ongoing
          if (newEnd === null) return existingEnd === null || existingEnd >= newStart;
          if (existingEnd === null) return newEnd >= existingStart;
          
          return newStart <= existingEnd && newEnd >= existingStart;
        });

        if (overlappingRules.length > 0) {
          errors.push(`Overlapping ${newRule.rule_type} rule found for the specified date range`);
        }
      }

      // Validate rate values
      if (newRule.calculation_method === 'fixed_amount' && (!newRule.base_rate || newRule.base_rate <= 0)) {
        errors.push('Base rate must be greater than 0 for fixed amount calculations');
      }

      if (newRule.calculation_method === 'percentage' && (!newRule.percentage_rate || newRule.percentage_rate < 0 || newRule.percentage_rate > 100)) {
        errors.push('Percentage rate must be between 0 and 100');
      }

      return {
        valid: errors.length === 0,
        errors
      };
    } catch (error) {
      console.error('[compensationService] Error validating rules:', error);
      return {
        valid: false,
        errors: ['Failed to validate compensation rules']
      };
    }
  },

  /**
   * Generate payroll preview for multiple employees
   * Refactored to use centralized time entry analytics
   */
  generatePayrollPreview: async (input: PayrollPreviewInput): Promise<PayrollPreviewResult[]> => {
    try {
      const results: PayrollPreviewResult[] = [];
      
      // Check for active service charge period
      let servicChargePeriod = null;
      try {
        servicChargePeriod = await serviceChargeService.getActivePeriod(input.startDate, input.endDate);
      } catch (scError) {
        console.warn('[compensationService] Service charge period check failed:', scError);
      }

      // Fetch company settings for split-shift configuration once
      let splitShiftConfig: SplitShiftConfig = { ...DEFAULT_SPLIT_SHIFT_CONFIG };
      
      if (input.profileIds.length > 0) {
        const { data: fetchedSettings } = await supabase.rpc('get_company_settings');
        if (fetchedSettings?.split_shift_config) {
          splitShiftConfig = fetchedSettings.split_shift_config as unknown as SplitShiftConfig;
        }
      }
      
      // Store analytics for service charge calculation
      const employeeAnalytics = new Map<string, { analytics: any; name: string }>();

      for (const profileId of input.profileIds) {
        // Get employee profile information (including contract details for salary conversion)
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, company_id, weekly_hours, fte')
          .eq('id', profileId)
          .single();

        if (profileError || !profile) {
          console.warn(`Profile not found for ID: ${profileId}`);
          continue;
        }

        // Get company settings for default weekly hours
        const { data: companySettings } = await supabase
          .from('company_settings')
          .select('business_hours')
          .eq('company_id', profile.company_id)
          .single();

        // Simple constant fallback for salary calculations (no business hours dependency)
        const companyWeeklyHours = 40; // Standard full-time weekly hours

        // Get assigned rules to check for overtime configuration
        const assignedRules = await compensationService.getAssignedRules(profileId, { activeOnly: true });
        const overtimeRule = assignedRules.find(r => r.rule_type === 'overtime' && r.is_active);
        
        // Configure overtime thresholds from rule or use defaults
        const dailyThreshold = overtimeRule?.threshold_hours || 8;
        const weeklyThreshold = overtimeRule?.weekly_hours || 40;
        const overtimeTrigger = overtimeRule?.overtime_trigger || 'daily_hours';

        // Use centralized time entry analytics for aggregation
        const analytics = await timeEntryAnalytics.analyzeTimeEntries({
          profileId,
          startDate: input.startDate,
          endDate: input.endDate,
          excludeWeekends: false,
          excludeHolidays: false,
          minimumHoursPerDay: 0,
          splitShiftConfig
        });

        // Calculate overtime based on compensation rule thresholds
        // (analytics provides daily breakdown; we apply rule-specific logic here)
        let overtimeHours = 0;
        const dailyOvertimeHours: number[] = [];
        const dailyOvertimeDetails: Array<{
          hours: number;
          date: string;
          isScheduledDay: boolean;
        }> = [];
        
        if (overtimeTrigger === 'daily_hours' || overtimeTrigger === 'both') {
          // Daily overtime - track per day for accurate tiered calculations
          // EXCLUDE holiday days from overtime calculation (they're paid separately as holiday hours)
          for (const day of analytics.dayAnalytics) {
            // Skip holiday days - they don't count toward overtime thresholds
            if (day.isHoliday) {
              continue;
            }
            
            if (day.totalHours > dailyThreshold) {
              const dayOT = day.totalHours - dailyThreshold;
              overtimeHours += dayOT;
              dailyOvertimeHours.push(dayOT);
              
              // NEW: Track which day and whether it's scheduled
              dailyOvertimeDetails.push({
                hours: dayOT,
                date: format(new Date(day.date), 'yyyy-MM-dd'),
                isScheduledDay: day.isBusinessDay
              });
            }
          }
        }
        
        if (overtimeTrigger === 'weekly_hours' || overtimeTrigger === 'both') {
          // Weekly overtime (subtract daily OT and holiday hours to avoid double-counting)
          // Holiday hours don't count toward weekly overtime thresholds
          const regularHours = analytics.totalHours - overtimeHours - analytics.holidayHours;
          if (regularHours > weeklyThreshold) {
            const weeklyOT = regularHours - weeklyThreshold;
            overtimeHours += weeklyOT;
            dailyOvertimeHours.push(weeklyOT);
            
            // NEW: Weekly OT is always "scheduled" (across the whole week)
            dailyOvertimeDetails.push({
              hours: weeklyOT,
              date: 'weekly',
              isScheduledDay: true // Weekly OT should use standard rule
            });
          }
        }

        // Construct rate context for proper salary-to-hourly conversion
        const rateContext: RateContext = {
          employeeWeeklyHours: profile.weekly_hours,
          // TODO: FTE temporarily omitted (hard-coded to 1.0 in resolveWeeklyHours) — future: source from pay policy/template
          companyWeeklyHours,
          weeksPerYear: 52.1429 // Standard precision for salary calculations
        };

        // Get compensation rules for allowance calculation
        const rules = await compensationService.getEmployeeCompensationRulesEnhanced({
          profileId,
          effectiveDate: input.endDate,
          activeOnly: true
        });

        // Check if salary rule applies to holidays
        const salaryRule = rules.find(r => r.rule_type === 'salary');
        const appliesToHolidays = salaryRule?.applies_to_holidays ?? true;

        // FIX: Only subtract overtime for hourly rate scenarios
        // For salary rules with OT tiers, the calculation engine handles hour allocation
        const salaryRuleWithTiers = rules.find(r => 
          r.rule_type === 'salary' && 
          r.ot_tiers && 
          Array.isArray(r.ot_tiers) && 
          r.ot_tiers.length > 0
        );

        const adjustedAnalyticsRegularHours = salaryRuleWithTiers 
          ? analytics.regularHours  // Don't adjust for salary with OT tiers
          : Math.max(0, analytics.regularHours - overtimeHours); // Adjust for hourly rate

        devLog('[Overtime Double-Count Fix]:', {
          analyticsRegularHours: analytics.regularHours,
          recalculatedOvertimeHours: overtimeHours,
          hasSalaryWithTiers: !!salaryRuleWithTiers,
          adjustedRegularHours: adjustedAnalyticsRegularHours
        });

        // Reclassify holiday hours as regular hours if not paid separately
        let adjustedRegularHours = adjustedAnalyticsRegularHours; // Use the OT-adjusted value
        let adjustedHolidayHours = analytics.holidayHours;
        let adjustedTotalHours = analytics.totalHours;

        if (!appliesToHolidays && salaryRule && analytics.holidayHours > 0) {
          // For salary rules that don't pay holidays separately,
          // treat holiday hours as regular hours (they're covered by base salary)
          adjustedRegularHours = analytics.regularHours + analytics.holidayHours;
          adjustedHolidayHours = 0;
          
          console.info(`[Compensation] Reclassifying ${analytics.holidayHours} holiday hours as regular hours for salary calculation (applies_to_holidays=false)`);
        }

        // Build daily hours breakdown for salary OT calculation
        const dailyHoursBreakdown = analytics.dayAnalytics.map(day => ({
          date: format(new Date(day.date), 'yyyy-MM-dd'),
          dayIndex: new Date(day.date).getDay(), // 0 = Sunday, 6 = Saturday
          hours: day.totalHours,
          isWeekend: day.isWeekend,
          isHoliday: day.isHoliday,
          isBusinessDay: day.isBusinessDay // NEW: Include business day flag
        }));

        // Get paid leave entries for this profile if available
        const paidLeaveEntries = input.paidLeaveMap?.get(profileId) || [];

        // Calculate compensation with proper rate context AND analytics for allowances
        const calculation = await calculationEngine.calculateCompensation(
          {
            profileId,
            hoursWorked: adjustedRegularHours + overtimeHours + adjustedHolidayHours,
            overtimeHours,
            dailyOvertimeHours,
            dailyOvertimeDetails, // NEW: Pass detailed breakdown
            dailyHoursBreakdown, // NEW: Pass daily breakdown for salary OT
            holidayHours: adjustedHolidayHours, // Use adjusted holiday hours (will be 0 if reclassified)
            payPeriodStart: input.startDate,
            payPeriodEnd: input.endDate,
            rateContext,
            currencyCode: input.currencyCode, // Pass currency code for line item descriptions
            paidLeaveEntries, // Pass leave data for inclusion BEFORE tax/deductions
          },
          rules,
          analytics // Phase 4: Pass analytics for allowance calculations
        );

        // Store analytics for service charge calculation
        employeeAnalytics.set(profileId, {
          analytics,
          name: `${profile.first_name} ${profile.last_name}`
        });

        // Get raw time entries for backward compatibility
        const timeEntries = analytics.dayAnalytics.flatMap(day => 
          day.shifts.map(shift => ({
            clock_in: shift.clockIn.toISOString(),
            clock_out: shift.clockOut.toISOString()
          }))
        );

        // Fetch YTD summary from actual payslip history
        const startOfYear = new Date(new Date().getFullYear(), 0, 1);
        const { data: ytdPayslips } = await supabase
          .from('payslips')
          .select('gross_pay, net_pay, total_hours')
          .eq('profile_id', profileId)
          .gte('start_date', startOfYear.toISOString())
          .lte('start_date', input.endDate.toISOString());

        const yearToDateSummary = {
          total_gross: ytdPayslips?.reduce((sum, p) => sum + Number(p.gross_pay || 0), 0) || 0,
          total_net: ytdPayslips?.reduce((sum, p) => sum + Number(p.net_pay || 0), 0) || 0,
          total_hours: ytdPayslips?.reduce((sum, p) => sum + Number(p.total_hours || 0), 0) || 0,
          periods_count: ytdPayslips?.length || 0
        };

        results.push({
          profileId,
          employeeName: `${profile.first_name} ${profile.last_name}`,
          calculation,
          timeEntries,
          yearToDateSummary
        });
      }
      
      // Calculate and integrate service charge distribution
      if (servicChargePeriod && employeeAnalytics.size > 0) {
        try {
          const scDistributions = await serviceChargeService.calculateDistribution(
            servicChargePeriod,
            employeeAnalytics
          );
          
          // Merge service charge line items into calculations
          for (const result of results) {
            const scDist = scDistributions.get(result.profileId);
            if (scDist) {
              result.calculation.lineItems.push(scDist.lineItem);
              result.calculation.totalEarnings += scDist.distributionAmount;
              result.calculation.totalGrossPay += scDist.distributionAmount;
              result.calculation.netPay += scDist.distributionAmount;
            }
          }
        } catch (scError) {
          const errorMessage = scError instanceof Error ? scError.message : 'Unknown error';
          console.error('[compensationService] Service charge calculation failed:', scError);
          input.onServiceChargeWarning?.(`Service charge calculation failed: ${errorMessage}. Preview totals do not include service charge payouts.`);
        }
      }

      return results;
    } catch (error) {
      console.error('[compensationService] Error generating payroll preview:', error);
      throw new Error(`Failed to generate payroll preview: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },


  // ========== NEW ASSIGNMENT-BASED METHODS ==========

  /**
   * Get rules assigned to employees via the secure database function
   */
  getAssignedRules: async (profileId: string, options: { ruleType?: CompensationRule['rule_type']; activeOnly?: boolean; effectiveDate?: Date } = {}): Promise<CompensationRule[]> => {
    try {
      // The RPC filters by effective date server-side (defaults to today when omitted),
      // so historical periods must pass the date explicitly.
      const { data, error } = await supabase.rpc('get_employee_active_assignments', {
        p_profile_id: profileId,
        ...(options.effectiveDate
          ? { p_effective_date: format(options.effectiveDate, 'yyyy-MM-dd') }
          : {}),
      } as any);

      if (error) {
        console.error('[compensationService] Error fetching assigned rules:', error);
        throw new Error(`Failed to fetch assigned rules: ${error.message}`);
      }

      // Transform the result to match CompensationRule interface (Phase 4: Add allowance fields)
      let rules = (data || []).map((assignment: any) => ({
        id: assignment.rule_id,
        company_id: assignment.company_id,
        rule_name: assignment.rule_name,
        rule_type: assignment.rule_type,
        description: '', // Not included in the secure function result
        base_rate: assignment.base_rate,
        percentage_rate: assignment.percentage_rate,
        multiplier_rate: assignment.multiplier_rate,
        threshold_hours: assignment.threshold_hours,
        priority_order: assignment.priority_order,
        effective_from: assignment.effective_from,
        effective_to: assignment.effective_to,
        is_active: true, // Only active rules are returned by the secure function
        calculation_method: assignment.calculation_method || 'fixed_amount',
        created_at: new Date().toISOString(), // Placeholder
        updated_at: new Date().toISOString(), // Placeholder
        created_by: null,
        applies_to_holidays: assignment.applies_to_holidays || false,
        applies_to_overtime: assignment.applies_to_overtime || false,
        overtime_trigger: assignment.overtime_trigger || 'daily_hours',
        threshold_amount: assignment.threshold_amount || 0,
        tiered_rates: assignment.tiered_rates || [],
        weekly_hours: assignment.weekly_hours ?? null,
        // Phase 4: Allowance-specific fields
        allowance_type: assignment.allowance_type || null,
        allowance_eligibility_criteria: assignment.allowance_eligibility_criteria || {},
        min_hours_per_day: assignment.min_hours_per_day || null,
        applies_to_weekends: assignment.applies_to_weekends || false,
        // Tax/Deduction-specific fields (CRITICAL FIX)
        deduction_category: assignment.deduction_category || null,
        deduction_type: assignment.deduction_type || null,
        tax_brackets: assignment.tax_brackets || null,
        tax_notes: assignment.tax_notes || '',
        // Salary schedule fields (for non-scheduled day OT)
        workweek_days: assignment.workweek_days ?? null,
        scheduled_days: assignment.scheduled_days ?? null,
        ot_tiers: assignment.ot_tiers ?? null,
        holiday_multiplier: assignment.holiday_multiplier ?? null,
        salary_mode: assignment.salary_mode || 'annual',
      } as CompensationRule));

      // Apply filters
      if (options.ruleType) {
        rules = rules.filter(rule => rule.rule_type === options.ruleType);
      }

      return rules;
    } catch (error) {
      console.error('[compensationService] Error in getAssignedRules:', error);
      throw error;
    }
  },

  /**
   * Assign a compensation rule to multiple employees
   * 
   * REVISED: Uses explicit check → update/insert logic to avoid 409 Conflict errors
   * when re-assigning previously deactivated rules. Implements FMEA mitigations:
   * - F1: Batch check existing assignments in single query
   * - F2: Try-catch for UNIQUE constraint fallback
   * - F3: Separate audit logging for reactivation vs new assignment
   * - F4: Atomic batch operations where possible
   * - F5: Idempotent - returns success even if already active
   */
  assignRuleToEmployees: async (ruleId: string, profileIds: string[], assignedBy?: string): Promise<any[]> => {
    try {
      // Get rule details for company verification
      const { data: rule, error: ruleError } = await supabase
        .from('compensation_rules')
        .select('company_id')
        .eq('id', ruleId)
        .single();

      if (ruleError || !rule) {
        throw new Error('Rule not found');
      }

      // Get current user for assigned_by if not provided
      let effectiveAssignedBy = assignedBy;
      if (!effectiveAssignedBy) {
        const { data: userData } = await supabase.auth.getUser();
        effectiveAssignedBy = userData?.user?.id;
      }

      // STEP 1: Batch check existing assignments (single query - addresses F1)
      const { data: existingAssignments, error: checkError } = await supabase
        .from('compensation_rule_assignments')
        .select('id, profile_id, is_active')
        .eq('rule_id', ruleId)
        .in('profile_id', profileIds);

      if (checkError) {
        console.error('[compensationService] Error checking existing assignments:', checkError);
        throw new Error(`Failed to check existing assignments: ${checkError.message}`);
      }

      // Create lookup map for existing assignments
      const existingMap = new Map<string, { id: string; is_active: boolean }>();
      (existingAssignments || []).forEach(a => {
        existingMap.set(a.profile_id, { id: a.id, is_active: a.is_active });
      });

      // STEP 2: Categorize into: reactivate, skip (already active), insert (new)
      const toReactivate: string[] = [];  // assignment IDs to update
      const toInsert: string[] = [];      // profile IDs to insert
      const alreadyActive: string[] = []; // profile IDs already active (skip)
      const reactivatedProfiles: string[] = []; // for audit logging

      for (const profileId of profileIds) {
        const existing = existingMap.get(profileId);
        if (existing) {
          if (existing.is_active) {
            alreadyActive.push(profileId); // F5: Idempotent - skip but don't error
          } else {
            toReactivate.push(existing.id);
            reactivatedProfiles.push(profileId);
          }
        } else {
          toInsert.push(profileId);
        }
      }

      const results: any[] = [];
      const now = new Date().toISOString();

      // STEP 3a: Batch reactivate inactive assignments (addresses F4)
      if (toReactivate.length > 0) {
        const { data: reactivated, error: reactivateError } = await supabase
          .from('compensation_rule_assignments')
          .update({ 
            is_active: true, 
            assigned_at: now,
            assigned_by: effectiveAssignedBy 
          })
          .in('id', toReactivate)
          .select();

        if (reactivateError) {
          console.error('[compensationService] Error reactivating assignments:', reactivateError);
          throw new Error(`Failed to reactivate assignments: ${reactivateError.message}`);
        }

        results.push(...(reactivated || []));

        // F3: Log reactivation separately for audit clarity
        if (reactivatedProfiles.length > 0) {
          await logAssignmentHistory(
            'assigned',
            ruleId,
            reactivatedProfiles,
            `Reactivated assignment for ${reactivatedProfiles.length} employee(s)`
          );
        }
      }

      // STEP 3b: Batch insert new assignments (addresses F4)
      if (toInsert.length > 0) {
        const newAssignments = toInsert.map(profileId => ({
          rule_id: ruleId,
          profile_id: profileId,
          company_id: rule.company_id,
          assigned_by: effectiveAssignedBy,
          assigned_at: now,
          is_active: true
        }));

        try {
          const { data: inserted, error: insertError } = await supabase
            .from('compensation_rule_assignments')
            .insert(newAssignments)
            .select();

          if (insertError) {
            // F2: Handle UNIQUE constraint violation gracefully (race condition fallback)
            if (insertError.code === '23505') { // PostgreSQL unique violation
              console.warn('[compensationService] UNIQUE constraint hit during insert, falling back to individual upserts');
              // Fall back to individual operations for conflicting rows
              for (const assignment of newAssignments) {
                const { data: single } = await supabase
                  .from('compensation_rule_assignments')
                  .upsert(assignment, { onConflict: 'rule_id,profile_id' })
                  .select()
                  .single();
                if (single) results.push(single);
              }
            } else {
              throw new Error(`Failed to insert assignments: ${insertError.message}`);
            }
          } else {
            results.push(...(inserted || []));
          }
        } catch (insertCatch) {
          console.error('[compensationService] Error inserting assignments:', insertCatch);
          throw insertCatch;
        }

        // Log new assignments for audit
        if (toInsert.length > 0) {
          await logAssignmentHistory(
            toInsert.length > 1 ? 'bulk_assigned' : 'assigned',
            ruleId,
            toInsert,
            `Assigned to ${toInsert.length} new employee(s)`
          );
        }
      }

      // F5: Log summary including already-active for transparency
      if (alreadyActive.length > 0) {
        devLog(`[compensationService] ${alreadyActive.length} employee(s) already had active assignment - skipped`);
      }

      devLog(`[compensationService] Assignment complete: ${toReactivate.length} reactivated, ${toInsert.length} new, ${alreadyActive.length} already active`);

      return results;
    } catch (error) {
      console.error('[compensationService] Error in assignRuleToEmployees:', error);
      throw error;
    }
  },

  /**
   * Remove rule assignment from an employee
   */
  unassignRule: async (ruleId: string, profileId: string): Promise<boolean> => {
    try {
      // Atomic: row-locks the assignment, deactivates it and writes the audit
      // history entry in a single transaction (Domain 9 remediation).
      const { data, error } = await supabase.rpc('unassign_compensation_rule', {
        p_rule_id: ruleId,
        p_profile_id: profileId,
        p_reason: 'Rule unassigned from employee',
      });

      if (error) {
        console.error('[compensationService] Error unassigning rule:', error);
        throw new Error(`Failed to unassign rule: ${error.message}`);
      }

      const result = data as unknown as {
        success: boolean;
        changed?: boolean;
        reason?: string;
        error?: string;
      } | null;

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to unassign rule');
      }

      if (result.changed === false) {
        devLog(`[compensationService] Unassign no-op for rule ${ruleId}: ${result.reason}`);
      }

      return true;
    } catch (error) {
      console.error('[compensationService] Error in unassignRule:', error);
      throw error;
    }
  },

  /**
   * Get all employees assigned to a specific rule
   */
  getRuleAssignments: async (ruleId: string): Promise<any[]> => {
    try {
      const { data, error } = await supabase
        .from('compensation_rule_assignments')
        .select(`
          id,
          profile_id,
          assigned_at,
          is_active,
          profiles (
            id,
            first_name,
            last_name,
            email,
            role
          )
        `)
        .eq('rule_id', ruleId)
        .eq('is_active', true)
        .order('assigned_at', { ascending: false });

      if (error) {
        console.error('[compensationService] Error fetching rule assignments:', error);
        throw new Error(`Failed to fetch rule assignments: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error('[compensationService] Error in getRuleAssignments:', error);
      throw error;
    }
  },

  /**
   * Enhanced employee compensation rules that supports both legacy and assignment models
   */
  getEmployeeCompensationRulesEnhanced: async (query: CompensationRuleQuery): Promise<CompensationRule[]> => {
    try {
      // Try assignment-based approach first
      const assignedRules = await compensationService.getAssignedRules(query.profileId, {
        ruleType: query.ruleType as CompensationRule['rule_type'],
        activeOnly: query.activeOnly,
        effectiveDate: query.effectiveDate,
      });

      return assignedRules;
    } catch (error) {
      console.error('[compensationService] Error in getEmployeeCompensationRulesEnhanced:', error);
      return [];
    }
  },

  /**
   * Helper function to determine if a rule type is mutually exclusive
   * Only salary and hourly_rate should have at most ONE active assignment per employee
   */
  isMutuallyExclusiveRuleType: (ruleType: string): boolean => {
    const exclusiveTypes = ['salary', 'hourly_rate'];
    return exclusiveTypes.includes(ruleType);
  },

  /**
   * Validate rule assignments for conflicts
   * Only checks for conflicts on mutually exclusive rule types (salary, hourly_rate)
   * Allows multiple allowances, bonuses, and deductions per employee
   */
  validateRuleAssignment: async (ruleId: string, profileIds: string[]): Promise<{ valid: boolean; errors: string[]; conflicts: any[] }> => {
    try {
      const errors: string[] = [];
      const conflicts: any[] = [];

      // Get rule details
      const { data: rule, error: ruleError } = await supabase
        .from('compensation_rules')
        .select('*')
        .eq('id', ruleId)
        .single();

      if (ruleError || !rule) {
        errors.push('Rule not found');
        return { valid: false, errors, conflicts };
      }

      // Determine if this rule type is mutually exclusive
      const isExclusiveType = compensationService.isMutuallyExclusiveRuleType(rule.rule_type);

      // Check for existing assignments and conflicts
      for (const profileId of profileIds) {
        // Check if this exact rule is already assigned
        const { data: existingAssignment } = await supabase
          .from('compensation_rule_assignments')
          .select('id')
          .eq('rule_id', ruleId)
          .eq('profile_id', profileId)
          .eq('is_active', true)
          .maybeSingle();

        if (existingAssignment) {
          errors.push(`Employee already has this rule assigned`);
          continue;
        }

        // Only check for date overlap conflicts if this is a mutually exclusive rule type
        // For non-exclusive types (allowance, bonus, deduction), multiple assignments are allowed
        if (isExclusiveType) {
          const existingRules = await compensationService.getAssignedRules(profileId, {
            ruleType: rule.rule_type as CompensationRule['rule_type'],
            activeOnly: true
          });

          const conflictingRules = existingRules.filter(existingRule => {
            if (existingRule.id === ruleId) return false;

            const ruleStart = new Date(rule.effective_from);
            const ruleEnd = rule.effective_to ? new Date(rule.effective_to) : null;
            const existingStart = new Date(existingRule.effective_from);
            const existingEnd = existingRule.effective_to ? new Date(existingRule.effective_to) : null;

            // Check for date overlap
            if (ruleEnd === null && existingEnd === null) return true;
            if (ruleEnd === null) return existingEnd === null || existingEnd >= ruleStart;
            if (existingEnd === null) return ruleEnd >= existingStart;
            
            return ruleStart <= existingEnd && ruleEnd >= existingStart;
          });

          if (conflictingRules.length > 0) {
            conflicts.push({
              profileId,
              reason: `Cannot assign multiple ${rule.rule_type} rules with overlapping dates`,
              conflictingRules: conflictingRules.map(r => ({
                id: r.id,
                rule_name: r.rule_name,
                effective_from: r.effective_from,
                effective_to: r.effective_to
              }))
            });
          }
        }
      }

      if (conflicts.length > 0) {
        errors.push(`Found ${conflicts.length} assignment conflict(s)`);
      }

      return {
        valid: errors.length === 0,
        errors,
        conflicts
      };
    } catch (error) {
      console.error('[compensationService] Error in validateRuleAssignment:', error);
      return {
        valid: false,
        errors: ['Failed to validate rule assignment'],
        conflicts: []
      };
    }
  }
};